import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { SuiteLayout, Glass, NeonButton } from "../../components/suite/SuiteLayout";
import { Plans } from "../../lib/plans";

// Landing after Stripe: confirms a plan trial (?plan_session=) or an add-on pack (?addon_session=).
export default function SuiteBillingReturn() {
  const [params] = useSearchParams();
  const planSid = params.get("plan_session");
  const addonSid = params.get("addon_session");
  const [state, setState] = useState({ phase: "checking" });

  useEffect(() => {
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const r = planSid ? await Plans.checkoutStatus(planSid) : await Plans.addonStatus(addonSid);
        const ok = planSid ? r.checkout_status === "completed" : r.payment_status === "paid";
        if (ok) return !stop && setState({ phase: "done", r });
        if (r.checkout_status === "expired" || r.payment_status === "expired") return !stop && setState({ phase: "failed" });
      } catch { /* keep polling */ }
      if (++tries < 15 && !stop) setTimeout(poll, 2000);
      else if (!stop) setState({ phase: "failed" });
    };
    if (planSid || addonSid) poll(); else setState({ phase: "failed" });
    return () => { stop = true; };
  }, [planSid, addonSid]);

  const r = state.r;
  return (
    <SuiteLayout credits={r?.available} title="Confirming">
      <div className="max-w-xl mx-auto px-4 pt-20">
        <Glass className="p-8 text-center" data-testid="billing-return">
          {state.phase === "checking" && <><Loader2 className="mx-auto animate-spin text-cyan-300" size={36} /><p className="mt-4 text-white font-display text-2xl font-bold">Confirming with Stripe…</p></>}
          {state.phase === "done" && (
            <>
              <CheckCircle2 className="mx-auto text-emerald-400" size={44} />
              <p className="mt-4 text-white font-display text-2xl font-bold" data-testid="billing-return-title">
                {planSid ? (r.status === "active" && !r.is_trial ? `${r.plan?.name} is active` : `Your ${r.plan?.name} trial has started`) : "100 credits added"}
              </p>
              <p className="mt-2 text-slate-300 text-sm" data-testid="billing-return-detail">
                {planSid ? (r.is_trial ? "You have 10 trial credits for 7 days. No charge until your plan starts." : `${r.plan_credits} credits ready this month.`)
                  : r.is_trial ? `100 add-on credits saved (${r.addon_credits} total). They become usable once your trial converts to your paid plan.` : `You now have ${r.available} usable credits.`}
              </p>
              <Link to={planSid ? "/batch" : "/billing"}><NeonButton className="mt-6" data-testid="billing-return-continue">{planSid ? "Start a batch" : "Back to Billing"}</NeonButton></Link>
            </>
          )}
          {state.phase === "failed" && (
            <>
              <AlertTriangle className="mx-auto text-amber-400" size={40} />
              <p className="mt-4 text-white font-display text-2xl font-bold">We couldn't confirm this payment</p>
              <p className="mt-2 text-slate-400 text-sm">If you completed checkout, refresh Billing in a minute.</p>
              <Link to="/billing"><NeonButton className="mt-6" data-testid="billing-return-back">Go to Billing</NeonButton></Link>
            </>
          )}
        </Glass>
      </div>
    </SuiteLayout>
  );
}
